import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAppIcon, getAppColor, formatCurrency, calculateUsagePercentage } from "@/lib/utils";
import { Application, License } from "@shared/schema";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { useLocation } from "wouter";

type AppDetailsProps = {
  app: Application;
  licenses: License[];
};

function AppDetails({ app, licenses }: AppDetailsProps) {
  // Find the primary license for this app
  const primaryLicense = licenses.find(license => license.applicationId === app.id);
  
  // Calculate usage percentage based on assigned vs total seats
  const usagePercentage = primaryLicense 
    ? calculateUsagePercentage(primaryLicense.assignedSeats, primaryLicense.totalSeats)
    : 0;
  
  // Calculate monthly cost - just using the cost of the primary license for now
  const monthlyCost = primaryLicense ? primaryLicense.cost : 0;
  
  // Determine status based on usage
  let status = "Active";
  let statusClass = "bg-green-100 text-green-800";
  
  if (usagePercentage < 50) {
    status = "Underutilized";
    statusClass = "bg-amber-100 text-amber-800";
  }

  return (
    <TableRow>
      <TableCell className="table-col-app">
        <div className="flex items-center space-x-3">
          <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${getAppColor(app.logo)} flex items-center justify-center flex-shrink-0`}>
            <i className={`${getAppIcon(app.logo)} text-sm sm:text-base`}></i>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{app.name}</div>
            <div className="text-xs text-slate-500 truncate">{app.vendor}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="table-col-category">
        <span className="text-sm text-slate-900 truncate block">{app.category}</span>
      </TableCell>
      <TableCell className="table-col-usage">
        <div className="w-full bg-slate-200 rounded-full h-1.5 max-w-[80px] sm:max-w-[100px]">
          <div 
            className={`${usagePercentage >= 70 ? 'bg-primary' : 'bg-amber-400'} h-1.5 rounded-full`} 
            style={{width: `${usagePercentage}%`}}
          ></div>
        </div>
        <div className="text-xs text-slate-500 mt-1 whitespace-nowrap">{usagePercentage}% active users</div>
      </TableCell>
      <TableCell className="table-col-cost">
        <span className="text-sm text-slate-900 whitespace-nowrap">{formatCurrency(monthlyCost)}</span>
      </TableCell>
      <TableCell className="table-col-status">
        <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${statusClass}`}>{status}</span>
      </TableCell>
    </TableRow>
  );
}

export default function TopApplications() {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [, navigate] = useLocation();
  
  // Handler functions to fix type issues with the pagination component
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  const handlePageSizeChange = (newPageSize: number) => {
    setItemsPerPage(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  const { data: apps = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });
  
  const { data: licenses = [], isLoading: licensesLoading } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });

  const isLoading = appsLoading || licensesLoading;
  
  // Calculate pagination
  const totalItems = apps.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageStartIndex = (page - 1) * itemsPerPage;
  const pageEndIndex = pageStartIndex + itemsPerPage;
  const currentPageItems = apps.slice(pageStartIndex, pageEndIndex);

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200 pb-4">
        <CardTitle className="text-lg font-semibold">Top Applications by Usage</CardTitle>
        <Button 
          variant="link" 
          size="sm" 
          className="text-primary font-medium"
          onClick={() => navigate('/applications')}
        >
          View All
        </Button>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-col-app">Application</TableHead>
                <TableHead className="table-col-category">Category</TableHead>
                <TableHead className="table-col-usage">Usage</TableHead>
                <TableHead className="table-col-cost">Monthly Cost</TableHead>
                <TableHead className="table-col-status">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(itemsPerPage)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="table-col-app">
                      <div className="flex items-center space-x-3">
                        <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded" />
                        <div className="min-w-0 flex-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16 mt-1" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="table-col-category">
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell className="table-col-usage">
                      <Skeleton className="h-1.5 w-[80px] sm:w-[100px] rounded-full" />
                      <Skeleton className="h-3 w-24 mt-1" />
                    </TableCell>
                    <TableCell className="table-col-cost">
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell className="table-col-status">
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                apps && licenses && 
                currentPageItems.map((app: Application) => (
                  <AppDetails key={app.id} app={app} licenses={licenses} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {!isLoading && apps && (
          <div className="border-t border-slate-200 px-3 py-2">
            <div className="flex justify-center">
              <DataTablePagination 
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
